import { useState, useEffect } from 'react';
import { X, Copy, Check, Send, Search, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FlowShareSheet({ flowId, onClose }) {
  const [friends, setFriends] = useState([]);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/flow?id=${flowId}`;

  useEffect(() => {
    fetchFriends();
  }, []);

  const fetchFriends = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(res.data);
    } catch {}
  };

  const sendToDevotee = async (friend) => {
    setSending(prev => ({ ...prev, [friend.id]: true }));
    try {
      const token = localStorage.getItem('dharmbook_token');
      const convRes = await axios.get(`${API}/conversations/${friend.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.post(`${API}/messages`, {
        conversation_id: convRes.data.id,
        content: `Shared a Pravah: ${shareUrl}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSent(prev => ({ ...prev, [friend.id]: true }));
      toast.success(`Sent to ${friend.name}`);
    } catch {
      toast.error('Failed to share');
    } finally {
      setSending(prev => ({ ...prev, [friend.id]: false }));
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const filtered = friends.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-lg bg-white rounded-t-2xl max-h-[60vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        onClick={e => e.stopPropagation()}
        data-testid="flow-share-sheet"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Share Pravah</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Devotees */}
        <div className="px-4 pt-3">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search devotees..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF9933]/40"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 space-y-1 max-h-40">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-4">
              {friends.length === 0 ? 'No devotees yet' : 'No match'}
            </p>
          ) : (
            filtered.map(f => (
              <div key={f.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                <img
                  src={f.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(f.name)}&background=FF9933&color=fff`}
                  alt={f.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="flex-1 text-sm font-medium text-gray-900 truncate">{f.name}</span>
                <button
                  onClick={() => sendToDevotee(f)}
                  disabled={sending[f.id] || sent[f.id]}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                    sent[f.id] ? 'bg-green-50 text-green-600' : 'bg-[#FF9933] hover:bg-[#E68A00] text-white'
                  }`}
                >
                  {sent[f.id] ? <><Check className="w-3.5 h-3.5" /> Sent</> : <><Send className="w-3.5 h-3.5" /> Send</>}
                </button>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-gray-100 p-4 space-y-2">
          <button onClick={copyLink} className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-800">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank')}
            className="w-full flex items-center gap-3 px-4 py-2.5 bg-green-50 hover:bg-green-100 rounded-lg text-sm font-medium text-green-700"
          >
            <ExternalLink className="w-4 h-4" /> Share via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}
